import { useEffect, type MouseEvent, type ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
  className?: string;
  titleClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
  overlayClassName?: string;
};

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[95vw] max-h-[95vh]',
};

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  showCloseButton = true,
  className = '',
  titleClassName = '',
  contentClassName = '',
  footerClassName = '',
  overlayClassName = '',
}: ModalProps) => {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) {
      onClose();
    }
  };

  const stopPropagation = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const modalContent = (
    <div
      className={`
        fixed inset-0 w-full h-full
        bg-black/40 backdrop-blur-sm
        flex items-center justify-center
        z-[1000] p-4
        animate-[fadeIn_0.2s_ease-out]
        ${overlayClassName}
      `}
      onClick={handleOverlayClick}
    >
      <div
        className={`
          bg-white rounded-2xl shadow-2xl
          flex flex-col
          w-full ${sizeClasses[size]}
          max-h-[90vh]
          animate-[slideUp_0.3s_ease-out]
          ${className}
        `}
        onClick={stopPropagation}
      >
        {(title !== undefined || showCloseButton) && (
          <div
            className={`
              flex items-center justify-between
              px-6 py-4
              border-b border-slate-200/80
              ${titleClassName}
            `}
          >
            <h3 className="text-lg font-bold text-slate-800 tracking-tight">
              {title}
            </h3>
            {showCloseButton && (
              <button
                className="
                  p-1.5 rounded-lg
                  text-slate-400 hover:text-slate-700
                  hover:bg-slate-100
                  transition-all duration-200
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                "
                onClick={onClose}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        <div
          className={`
            flex-1 overflow-y-auto
            px-6 py-5
            ${contentClassName}
          `}
        >
          {children}
        </div>

        {footer && (
          <div
            className={`
              flex items-center justify-end gap-3
              px-6 py-4
              border-t border-slate-200/80
              bg-slate-50/50
              rounded-b-2xl
              ${footerClassName}
            `}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default Modal;
